import { keepPreviousData, queryOptions } from '@tanstack/vue-query'
import type { Schemas } from '#shopware'

export const useProducts = () => {
  const { apiClient } = useShopwareContext()

  const getPaginatorOptions = (
    page: Ref<number | string>,
    categoryId: MaybeRef<string>,
    criteria: MaybeRefOrGetter<Schemas['Criteria']> = {},
    limit: MaybeRefOrGetter<number> = 10,
    order?: MaybeRefOrGetter<string>,
  ) => queryOptions<EntityListingResult<Schemas['Product']>>({
    queryKey: ['list-product', categoryId, page, criteria, limit, order],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await apiClient.invoke('readProductListing post /product-listing/{categoryId}', {
        headers: {
          'sw-include-seo-urls': true,
        },
        pathParams: {
          categoryId: unref(categoryId),
        },
        body: {
          ...toValue(criteria),
          p: page.value,
          limit: toValue(limit),
          order: toValue(order),
        },
      })
      return data as EntityListingResult<Schemas['Product']>
    },
    staleTime: 2000,
  })

  const searchPaginator = (
    search: Ref<string>,
    page: Ref<number | string>,
    criteria: MaybeRefOrGetter<Schemas['ProductListingCriteria']> = {},
  ) => queryOptions<EntityListingResult<Schemas['Product']>>({
    queryKey: ['search-product', search, page, criteria],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await apiClient.invoke('searchPage post /search', {
        headers: {
          'sw-include-seo-urls': true,
        },
        body: {
          ...toValue(criteria),
          search: search.value,
          p: Number(page.value),
        },
      })
      return data
    },
  })

  const productListPaginator = (
    page: Ref<number | string>,
    criteria: MaybeRefOrGetter<Schemas['ProductListingCriteria']> = {},
  ) => queryOptions<EntityListingResult<Schemas['Product']>>({
    queryKey: ['products', page, criteria],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const { data } = await apiClient.invoke('readProduct post /product', {
        headers: {
          'sw-include-seo-urls': true,
        },
        body: {
          ...toValue(criteria),
          page: Number(page.value),
        },
      })
      return data
    },
  })

  const getBundleCriteria = (): Schemas['Criteria'] => ({
    associations: {
      bundle: {
        associations: {
          part: {
            associations: {
              media: {},
              cover: {
                associations: {
                  media: {},
                },
              },
              seoUrls: {},
            },
          },
        },
      },
    },
  })

  return {
    getPaginatorOptions,
    getBundleCriteria,
    searchPaginator,
    productListPaginator,
  }
}
